import { createElement } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbArchive, TbArchiveOff, TbLock, TbLockOpen, TbStarFilled, TbStarOff } from 'react-icons/tb';
import { StateCreator } from 'zustand';

import { noteService } from '@/services';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    appendEntity,
    createSingleFlightKey,
    mergeEntity,
    mergeEntityById,
    removeEntityById,
    replaceEntityById,
    replaceOptimisticEntityById,
    runSingleFlight,
    upsertEntityById,
} from '@/stores/helpers/collections';
import { Note } from '@/types';

import { NoteActions, NoteState } from '../types';

export const createNoteActions: StateCreator<NoteState & NoteActions, [['zustand/immer', never]], [], NoteActions> = (
    set,
    get,
    store,
) => ({
    // Core
    fetchNotes: async (params) => {
        set({ loading: true });

        try {
            const notes = await noteService.list(params);

            set({ notes, loading: false, error: undefined });
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load notes' });
        }
    },
    fetchNoteById: async (id) => {
        set((state) => {
            state.loadingNote = true;
            state.error = undefined;
        });

        try {
            const note = await noteService.getById(id);

            set({ selectedNote: note, loadingNote: false });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.error = 'Failed to fetch note detail';
                state.loadingNote = false;
            });
        }
    },
    clearSelectedNote: () => {
        set((state) => {
            state.selectedNote = null;
        });
    },
    createNote: async (input) =>
        runSingleFlight(createSingleFlightKey('notes:create', input), async () => {
            const tempId = `temp-${Date.now()}`;
            const optimistic = { id: tempId, ...input } as Note;

            set({ notes: appendEntity(get().notes, optimistic) });

            try {
                const note = await noteService.create(input);

                set({ notes: replaceOptimisticEntityById(get().notes, tempId, note) });

                return note;
            } catch (error) {
                captureException(error);

                set({ notes: removeEntityById(get().notes, tempId), error: 'Failed to create note' });
            }
        }),
    updateNote: async (id, updates) => {
        const prev = get().notes.find((f) => f.id === id);
        if (!prev) {
            return;
        }

        set({
            notes: mergeEntityById(get().notes, id, updates),
            selectedNote:
                get().selectedNote?.id === id ? mergeEntity(get().selectedNote!, updates) : get().selectedNote,
        });

        try {
            const note = await noteService.update(id, updates);

            set({
                notes: mergeEntityById(get().notes, id, note),
                selectedNote:
                    get().selectedNote?.id === id ? mergeEntity(get().selectedNote!, note) : get().selectedNote,
            });
        } catch (error) {
            captureException(error);

            set({
                notes: replaceEntityById(get().notes, id, prev),
                selectedNote: get().selectedNote?.id === id ? prev : get().selectedNote,
                error: 'Failed to update note',
            });
        }
    },
    deleteNote: async (id) => {
        const prev = get().notes;

        set({
            notes: removeEntityById(get().notes, id),
            selectedNote: get().selectedNote?.id === id ? null : get().selectedNote,
        });

        try {
            await noteService.delete(id);
        } catch (error) {
            captureException(error);

            set({ notes: prev, error: 'Failed to delete note' });
        }
    },
    // - Aliases
    duplicateNote: async (id) => {
        const prev = get().notes;

        if (!prev) {
            return;
        }

        try {
            const duplicated = await noteService.duplicate(id);

            set({ notes: upsertEntityById(get().notes, duplicated, { prepend: true }) });

            toast(`Note Duplicated`, {
                icon: createElement(BiDuplicate, { size: '1rem' }),
                description: duplicated.title,
                resource: { id: duplicated.id, type: 'note' },
            });

            return duplicated;
        } catch (error) {
            captureException(error);

            set({ notes: prev, error: 'Failed to duplicate note' });

            toast.error('Failed to duplicate note');
        }
    },
    toggleNoteFavorite: async (id, favorited) => {
        const prev = get().notes.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const note = state.notes.find((f) => f.id === id);
            if (note) {
                note.favorited = favorited;
            }

            // Also update selectedNote if it matches
            if (state.selectedNote?.id === id) {
                state.selectedNote.favorited = favorited;
            }
        });

        try {
            await noteService.setFavorite(id, favorited);

            toast(`${favorited ? 'Added to' : 'Removed from'} Favorites`, {
                icon: createElement(favorited ? TbStarFilled : TbStarOff, { size: '1rem' }),
                description: prev.title,
                resource: { id, type: 'note' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const note = state.notes.find((f) => f.id === id);
                if (note) {
                    note.favorited = prev.favorited;
                }

                // Revert selectedNote favorited if it matches
                if (state.selectedNote?.id === id) {
                    state.selectedNote.favorited = prev.favorited;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    toggleNoteLock: async (id, locked) => {
        const prev = get().notes.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const note = state.notes.find((f) => f.id === id);
            if (note) {
                note.locked = locked;
            }

            // Also update selectedNote if it matches
            if (state.selectedNote?.id === id) {
                state.selectedNote.locked = locked;
            }
        });

        try {
            await noteService.setLocked(id, locked);

            toast(`Note ${locked ? 'Locked' : 'Unlocked'}`, {
                icon: createElement(locked ? TbLock : TbLockOpen, { size: '1rem' }),
                description: prev.title,
                resource: { id, type: 'note' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const note = state.notes.find((f) => f.id === id);
                if (note) {
                    note.locked = prev.locked;
                }

                // Revert selectedNote locked if it matches
                if (state.selectedNote?.id === id) {
                    state.selectedNote.locked = prev.locked;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    toggleNoteArchive: async (id, archived) => {
        const prev = get().notes.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const note = state.notes.find((f) => f.id === id);
            if (note) {
                note.archived = archived;
            }

            // Also update selectedNote if it matches
            if (state.selectedNote?.id === id) {
                state.selectedNote.archived = archived;
            }
        });

        try {
            await noteService.setArchived(id, archived);

            toast(`Note ${archived ? 'Archived' : 'Unarchived'}`, {
                icon: createElement(archived ? TbArchive : TbArchiveOff, { size: '1rem' }),
                description: prev.title,
                resource: { id, type: 'note' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const note = state.notes.find((f) => f.id === id);
                if (note) {
                    note.archived = prev.archived;
                }

                // Revert selectedNote archived if it matches
                if (state.selectedNote?.id === id) {
                    state.selectedNote.archived = prev.archived;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
});
